'use client';

import { useMergeRefs } from '@floating-ui/react';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { ComboboxRootContext } from './context/combobox-root.context';
import { comboboxInputStyles } from './styles/combobox-input.styles';
import type { ComboboxInputPropsInterface } from './types/combobox-input.types';

/**
 * @since 10.0.0
 */
export const ComboboxInput = forwardRef<HTMLInputElement, ComboboxInputPropsInterface>(
  (props: ComboboxInputPropsInterface, forwardedRef) => {
    const { className, css: cssProp, ...rest } = props;

    const {
      autoCompleteBehavior,
      combobox,
      controlId,
      descriptionId,
      disabled,
      placeholder,
      required,
      error,
    } = useContext(ComboboxRootContext);

    const mergedRefs = useMergeRefs([combobox.refs.setReference, forwardedRef]);

    const styles = css(comboboxInputStyles, cssProp);

    return (
      <input
        ref={mergedRefs}
        data-c11n-component="Combobox.Input"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', styles, className)}
        autoComplete="new-password"
        aria-autocomplete={autoCompleteBehavior === 'none' ? 'none' : 'list'}
        aria-describedby={descriptionId}
        aria-invalid={error || undefined}
        type="text"
        id={controlId}
        disabled={disabled}
        placeholder={placeholder}
        required={required}
        readOnly={!combobox.editable}
        value={combobox.inputValue}
        {...combobox.getReferenceProps({
          onChange: combobox.onChangeInput,
          onKeyDown: combobox.onKeyDownInput,
          onBlur: combobox.onBlurInput,
          ...rest,
        })}
      />
    );
  },
);

ComboboxInput.displayName = 'Combobox.Input';
